/**
 * @file consent-texts.ts
 * @description Тексты согласия на cookie, которые ставит платформа: форма
 * редакции баннера и окна о риске, встроенная редакция 1 и сборка текста
 * баннера (phase19.16, партия Р3г, v0.19.62; site-builder.md SB134 «фразу
 * про режим, список сервисов и кнопки ставит платформа», SB7, SB135 «версия
 * текста платформы входит в условия согласия», SB133 — окно о риске).
 *
 * С решения пользователя 2026-10-23 тексты — **редакции в базе**
 * (`SiteConsentTextRevision`, вид `BANNER` | `NOTICE_RISK`), их публикует
 * платформенный администратор без релиза (проект данных
 * docs/phases/phase19.16-materials/db-design-r3g-texts.md §1, §6). Поэтому
 * здесь нет «текущей версии»: её знает только база. Функции сборки чистые —
 * тексты нужной редакции им передаёт вызывающий (API по номеру
 * `counters.platformTextVersion` из снимка магазина, кабинет для
 * предпросмотра).
 *
 * Встроенная редакция 1 (`BUILT_IN_CONSENT_BANNER_TEXTS`,
 * `BUILT_IN_NOTICE_RISK_TEXTS`) — то, что вставляет миграция
 * `20261023130000_phase19_16_r3g_consent_texts`, и аварийный текст витрины,
 * когда блока `consent` в site-config нет. Не меняется никогда (золотой тест
 * сверяет её с JSON миграции). Поле `info` — дословно прежний текст
 * CookieBanner.tsx: после выкладки Р3г витрина магазина без счётчиков не
 * меняется (SB137).
 *
 * Модуль не импортирует реестр сервисов во время выполнения (только типы):
 * counters.ts берёт отсюда номер встроенной редакции, обратная зависимость
 * дала бы цикл модулей.
 */

import type { SiteConsentCategory } from "../site-theme/counters.js";

/** Вид редакции текстов согласия (= enum `SiteConsentTextKind` в schema.prisma). */
export const CONSENT_TEXT_KINDS = ["BANNER", "NOTICE_RISK"] as const;
export type ConsentTextKind = (typeof CONSENT_TEXT_KINDS)[number];

/**
 * Версия СОСТАВА ключей текстов (`SiteConsentTextRevision.schemaVersion`):
 * повышается, когда код добавляет поле или категорию.
 */
export const CONSENT_TEXTS_SCHEMA_VERSION = 1;

/** Номер встроенной редакции: `{}` в части COUNTERS читается как редакция 1. */
export const BUILT_IN_CONSENT_TEXT_VERSION = 1;

/** Категория в окне «Настроить»: название и что в неё входит. */
export interface ConsentCategoryTexts {
  readonly label: string;
  readonly description: string;
}

/** Фразы баннера одной редакции. Шаблоны с `{categories}` и `{services}` заполняет `buildConsentBannerText`. */
export interface ConsentBannerTexts {
  /** Вступление платформы, если владелец своего не написал (без точки в конце). */
  readonly introDefault: string;
  /** Фраза режима «Только после согласия» (без точки в конце). */
  readonly consentPhrase: string;
  /** Фраза режима «Уведомление» (без точки в конце). */
  readonly noticePhrase: string;
  /** Компактная полоса в 2 строки на /checkout, /order-confirm и в ЛК — режим «после согласия». */
  readonly compactConsent: string;
  /** Компактная полоса — режим «Уведомление». */
  readonly compactNotice: string;
  /** Уведомление без счётчиков (SB134). Оно же компактное. */
  readonly info: string;
  /** Ссылка на политику ПДн после текста. */
  readonly more: string;
  /** Родительный падеж категорий для фразы режима: «сервисы аналитики и рекламы». */
  readonly categoryGenitive: Readonly<Record<SiteConsentCategory, string>>;
  /** Окно «Настроить»: «Необходимые» (всегда) и категории сервисов. */
  readonly necessary: ConsentCategoryTexts;
  readonly categories: Readonly<Record<SiteConsentCategory, ConsentCategoryTexts>>;
  /** Описание «Аналитики», когда у Метрики включён Вебвизор (запись действий — другая обработка, SB133). */
  readonly analyticsWebvisorDescription: string;
  readonly buttons: {
    /** Режим «после согласия». */
    readonly acceptAll: string;
    readonly necessaryOnly: string;
    readonly configure: string;
    /** Режим «Уведомление» и уведомление без счётчиков. */
    readonly ok: string;
    readonly refuse: string;
    /** Окно «Настроить». */
    readonly save: string;
  };
  readonly settingsTitle: string;
  /** Подпись у «Необходимых» вместо переключателя. */
  readonly alwaysOn: string;
  /** Ссылка в нижней полосе подвала (SB136, SB17). */
  readonly settingsLink: string;
}

/** Окно о риске режима «Уведомление» (SB133): видит только владелец магазина. */
export interface ConsentNoticeRiskTexts {
  readonly title: string;
  readonly body: string;
  /** Подпись галочки «отвечаю сам». */
  readonly acknowledge: string;
}

/** Тексты по виду редакции. */
export interface ConsentTextsByKind {
  BANNER: ConsentBannerTexts;
  NOTICE_RISK: ConsentNoticeRiskTexts;
}

/** Встроенная редакция 1 баннера. НЕ МЕНЯТЬ: сверяется с миграцией и золотым тестом. */
export const BUILT_IN_CONSENT_BANNER_TEXTS: ConsentBannerTexts = Object.freeze<ConsentBannerTexts>({
  introDefault: "Мы используем cookie: необходимые — для корзины и входа",
  consentPhrase: "сервисы {categories} ({services}) — только с вашего согласия",
  noticePhrase: "сервисы {categories} ({services}) работают сразу, вы можете отказаться",
  compactConsent: "Мы используем cookie. Сервисы {categories} — только с вашего согласия.",
  compactNotice: "Мы используем cookie. Сервисы {categories} работают сразу, вы можете отказаться.",
  info: "Мы используем cookie для работы корзины и личного кабинета.",
  more: "Подробнее",
  categoryGenitive: Object.freeze({ analytics: "аналитики", ads: "рекламы" }),
  necessary: Object.freeze({
    label: "Необходимые",
    description: "Корзина, вход в личный кабинет и запоминание вашего выбора о cookie. Работают всегда.",
  }),
  categories: Object.freeze({
    analytics: Object.freeze({
      label: "Аналитика",
      description: "Статистика посещений: какие страницы открывают и откуда приходят.",
    }),
    ads: Object.freeze({
      label: "Реклама",
      description: "Оценка рекламы и показ объявлений тем, кто уже был на сайте.",
    }),
  }),
  analyticsWebvisorDescription:
    "Статистика посещений: какие страницы открывают и откуда приходят, и запись действий на страницах (Вебвизор).",
  buttons: Object.freeze({
    acceptAll: "Принять все",
    necessaryOnly: "Только необходимые",
    configure: "Настроить",
    ok: "Понятно",
    refuse: "Отказаться",
    save: "Сохранить выбор",
  }),
  settingsTitle: "Настройки cookie",
  alwaysOn: "Всегда включены",
  settingsLink: "Настройки cookie",
});

/** Встроенная редакция 1 окна о риске. НЕ МЕНЯТЬ. */
export const BUILT_IN_NOTICE_RISK_TEXTS: ConsentNoticeRiskTexts = Object.freeze<ConsentNoticeRiskTexts>({
  title: "Режим «Уведомление»",
  body:
    "Яндекс Метрика и VK Реклама начнут работать сразу, до того как посетитель согласился; " +
    "в баннере будет кнопка «Отказаться». Юрист ещё не ответил, достаточно ли этого по закону " +
    "о персональных данных. Если потребуется согласие, риск штрафа — на магазине. " +
    "Рекомендуем режим «Только после согласия».",
  acknowledge: "Понимаю риск и отвечаю за этот выбор сам",
});

/**
 * Формат номера редакции: целое ≥ 1. Существует ли такая редакция, знает
 * только база — проверяет API (422 `site_counters_text_version_unknown`).
 */
export function isConsentTextVersion(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 1;
}

// ── Сборка текста баннера ──

/** Вид баннера: согласие, уведомление о работающих счётчиках, уведомление без счётчиков. */
export type SiteConsentBannerKind = "consent" | "notice" | "info";

/** Категория, которая есть на сайте, и названия её сервисов в порядке реестра. */
export interface SiteConsentCategoryServices {
  readonly id: SiteConsentCategory;
  readonly services: readonly string[];
}

export interface BuildConsentBannerTextInput {
  readonly kind: SiteConsentBannerKind;
  /** Вступление владельца; пустое — вступление платформы. Для `info` не используется. */
  readonly intro: string;
  /** Категории с сервисами (для `info` — пусто). */
  readonly categories: readonly SiteConsentCategoryServices[];
}

export interface ConsentBannerText {
  /** Вступление (владельца или платформы); у `info` — текст уведомления. */
  readonly intro: string;
  /** Фраза режима со списком сервисов; у `info` — null. */
  readonly modeText: string | null;
  /** Итог для полосы и карточки: вступление + фраза режима. */
  readonly text: string;
  /** Компактная полоса в 2 строки (/checkout, /order-confirm, ЛК). */
  readonly compactText: string;
}

/** «аналитики и рекламы», «аналитики, рекламы и …» — список по-русски. */
function joinRu(items: readonly string[]): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} и ${items[items.length - 1]}`;
}

/** Меняет первое вхождение каждой вставки — поэтому проверка требует их ровно по одной. */
function fill(template: string, values: { categories: string; services: string }): string {
  return template.replace("{categories}", values.categories).replace("{services}", values.services);
}

const capitalize = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

/** Предложение заканчивается знаком конца — точку не дописываем. */
const SENTENCE_END = /[.!?…]$/u;

/**
 * Вступление и фраза режима — двумя предложениями: вступление владельца —
 * свободный текст, и склейка запятой ломалась бы на его последнем знаке.
 */
export function joinConsentSentences(intro: string, modeText: string): string {
  const head = intro.trim();
  const tail = `${capitalize(modeText.trim())}.`;
  if (head === "") return tail;
  return `${SENTENCE_END.test(head) ? head : `${head}.`} ${tail}`;
}

/**
 * Итоговый текст баннера из вступления владельца и фраз редакции `texts`
 * (SB134). Владелец не может стереть смысл согласия: фраза режима и список
 * сервисов всегда от платформы.
 */
export function buildConsentBannerText(texts: ConsentBannerTexts, input: BuildConsentBannerTextInput): ConsentBannerText {
  if (input.kind === "info" || input.categories.length === 0) {
    return { intro: texts.info, modeText: null, text: texts.info, compactText: texts.info };
  }
  const values = {
    categories: joinRu(input.categories.map((c) => texts.categoryGenitive[c.id])),
    services: input.categories.flatMap((c) => c.services).join(", "),
  };
  const own = input.intro.trim();
  const intro = own === "" ? texts.introDefault : own;
  const modeText = fill(input.kind === "consent" ? texts.consentPhrase : texts.noticePhrase, values);
  const compactText = fill(input.kind === "consent" ? texts.compactConsent : texts.compactNotice, values);
  // Вступление платформы — одной фразой через запятую, как в SB134; своё — двумя предложениями.
  const text = own === "" ? `${intro}, ${modeText}.` : joinConsentSentences(intro, modeText);
  return { intro, modeText, text, compactText };
}
